import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import "./status-picker.scss";
import { BoardStatus, StatusCategory } from "./types";

// A pickable action: a target status, optionally labelled by a workflow transition's name. When a
// project has a workflow, options are its transitions (JIRA-style named actions); otherwise they're
// the statuses themselves.
export interface StatusPickerOption {
  id: string; // the target status id
  label: string; // transition name, or the status name when unrestricted
  category: StatusCategory; // target status category → dot color
  sublabel?: string; // e.g. the target status name when the label is a transition name
}

interface StatusPickerProps {
  status: BoardStatus | undefined; // the issue's current status (the trigger)
  options: StatusPickerOption[];
  onPick: (statusId: string) => void;
}

const CATEGORY_COLOR: Record<StatusCategory, string> = {
  todo: "var(--tone-grey-fg)",
  in_progress: "var(--tone-blue-fg)",
  done: "var(--tone-green-fg)"
};

const CATEGORY_BG: Record<StatusCategory, string> = {
  todo: "var(--tone-grey-fill)",
  in_progress: "var(--tone-blue-fill)",
  done: "var(--tone-green-fill)"
};

const CATEGORY_BORDER: Record<StatusCategory, string> = {
  todo: "var(--tone-grey-line)",
  in_progress: "var(--tone-blue-line)",
  done: "var(--tone-green-line)"
};

const CATEGORY_FG: Record<StatusCategory, string> = {
  todo: "var(--tone-grey-fg)",
  in_progress: "var(--tone-blue-fg)",
  done: "var(--tone-green-fg)"
};

const StatusPicker: React.FC<StatusPickerProps> = ({ status, options, onPick }) => {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close on any outside interaction. Capture phase so a stopPropagation deeper in the tree (the
  // drawer's own handlers) can't swallow it; also close on Escape and on scroll (the menu is
  // absolutely positioned, so a scroll would otherwise leave it floating).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: Event): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onScroll = (): void => setOpen(false);
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const category: StatusCategory = status?.category ?? "todo";

  return (
    <div ref={wrapperRef} className="kuika-status-picker">
      <button
        type="button"
        className="kuika-status-picker__trigger"
        style={{
          background: CATEGORY_BG[category],
          borderColor: CATEGORY_BORDER[category],
          color: CATEGORY_FG[category]
        }}
        onClick={() => setOpen((p) => !p)}
        data-testid="status-picker-trigger"
      >
        <span className="kuika-status-picker__dot" style={{ background: CATEGORY_COLOR[category] }} />
        {status?.name ?? "No status"}
        <span className="kuika-status-picker__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="kuika-status-picker__menu" role="menu">
          {options.length === 0 ? (
            <div className="kuika-status-picker__empty">No available transitions</div>
          ) : (
            options.map((option: StatusPickerOption) => (
              <button
                key={`${option.id}:${option.label}`}
                type="button"
                role="menuitem"
                data-testid={`status-option-${option.category}`}
                className={classNames("kuika-status-picker__option", {
                  "kuika-status-picker__option--active": option.id === status?.id
                })}
                onClick={() => {
                  onPick(option.id);
                  setOpen(false);
                }}
              >
                <span className="kuika-status-picker__dot" style={{ background: CATEGORY_COLOR[option.category] }} />
                <span className="kuika-status-picker__option-name">{option.label}</span>
                <span className="kuika-status-picker__option-cat">
                  {option.sublabel ?? option.category.replace("_", " ")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default StatusPicker;
